import React from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { sizes } from '@/common/styles/sizes';
import { typography } from '@/common/styles/typographyTokens';

interface LoadingModalProps {
  isOpen: boolean;
  message?: string;
  /** 부가 안내 (대용량 업로드 등) */
  description?: string;
}

/**
 * 전역 로딩 오버레이. 파일 업로드처럼 오래 걸리는 작업에 사용한다.
 */
const LoadingModal: React.FC<LoadingModalProps> = ({
  isOpen,
  message = '파일 업로드 중입니다...',
  description,
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay role="status" aria-live="polite" aria-busy="true">
      <ModalContent>
        <Spinner />
        <LoadingText>{message}</LoadingText>
        {description && <Description>{description}</Description>}
        <ProgressTrack aria-hidden>
          <ProgressBar />
        </ProgressTrack>
      </ModalContent>
    </ModalOverlay>
  );
};

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const progressSlide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background-color: ${semanticColors.background.secondary};
  border-radius: ${sizes.radius.xl};
  padding: ${sizes.padding[24]} ${sizes.padding[28]};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: min(400px, calc(100vw - 2rem));
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${semanticColors.border.primary};
  border-top: 4px solid ${semanticColors.background.brand};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  margin: ${sizes.margin[16]} 0 0;
  ${typography.paragraph.p1.medium};
  color: ${semanticColors.text.primary};
  text-align: center;
`;

const Description = styled.p`
  margin: ${sizes.margin[8]} 0 0;
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.tertiary};
  text-align: center;
  line-height: 1.5;
  white-space: pre-line;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 4px;
  margin-top: ${sizes.margin[20]};
  border-radius: ${sizes.radius.full};
  background: ${semanticColors.background.tertiary};
  overflow: hidden;
`;

const ProgressBar = styled.div`
  width: 40%;
  height: 100%;
  border-radius: ${sizes.radius.full};
  background: ${semanticColors.background.brand};
  animation: ${progressSlide} 1.2s ease-in-out infinite;
`;

export default LoadingModal;
